import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Col, Input, Label, Modal, ModalBody, ModalFooter } from 'reactstrap';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Icon from '@mdi/react';
import { mdiClose } from '@mdi/js';
import { initialValues, validationSchema } from './constants';
import {
  getAuthUser,
  handlePostLogin,
  getRememberMe,
  addImpersonateUser,
} from '../../../../utils/auth.util';
import { changeEmployee } from '../../../../store/apis/userService';
import { fetchEmployeeDetails } from '../../../../store/apis/employeeService';
import * as icons from '../../../../assets';

import './ChangeEmployeeModal.scss';

function ChangeEmployeeModal({ isOpen, toggle }) {
  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: initialValues,
    mode: 'onSubmit',
    resolver: yupResolver(validationSchema),
  });

  const fetchEmployeeDataMutation = useMutation({
    mutationFn: fetchEmployeeDetails,
  });

  // Use useMutation for changeEmployee
  const { mutate: changeEmployeeMutation, isPending } = useMutation({
    mutationFn: changeEmployee,
    onSuccess: async (resp, params) => {
      const employeeDetailsArray = await fetchEmployeeDataMutation.mutateAsync({
        type: 'PROFILE',
        employeeId: params.employeeNo.trim().toLowerCase(),
      });

      const employeeDetails = employeeDetailsArray?.[0];
      if (!employeeDetails || employeeDetails.length === 0) {
        toast.error('Employee not found. Please check the Employee No.', {
          className: 'toast-error',
        });
        return;
      }
      const flag = addImpersonateUser({
        employeeId: employeeDetails?.EmployeeNo.trim().toLowerCase(),
        userName: employeeDetails?.EmployeeName,
      });

      if (!flag) {
        return;
      }
      handlePostLogin(resp?.data, '/dashboard', getRememberMe() || false);
      toggle();
    },
    onError: error => {
      toast.error(error?.message || 'Failed to change an employee no.', {
        className: 'toast-error',
      });
    },
  });

  const onSubmit = async data => {
    try {
      clearErrors();
      const realUser = getAuthUser();
      const employeeId = realUser?.employeeId?.trim().toLowerCase();
      const employeeNo = data?.employeeNo?.trim().toLowerCase();
      if (employeeId === employeeNo) {
        setError('employeeNo', {
          type: 'manual',
          message:
            'You have entered your current Employee No. Please enter a different Employee No.',
        });
        return;
      }
      changeEmployeeMutation({ employeeNo: data.employeeNo });
      // handle response as needed
    } catch (error) {
      toast.error(error?.message || 'Failed to change an employee no.', {
        className: 'toast-error',
      });
      toggle();
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xs">
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody className="w-100 d-flex flex-column align-items-start gap-3">
          <div className="d-flex flex-row align-items-center justify-content-between w-100">
            <div className="change-emp-icon">
              <img
                src={icons.iconsImpersonate}
                height="17px"
                alt="Impersonate Icon"
              />
            </div>
            <button type="button" onClick={toggle} className="non-btn">
              <Icon path={mdiClose} size={0.7} />
            </button>
          </div>
          <div>
            <span className="fw-semibold fs-5">Change Employee No.</span>
          </div>
          <div className="d-flex flex-column gap-1 w-100">
            <Label className="fw-medium" for="employeeNo">
              <span>Enter Employee Number</span>
              <span className="text-danger">*</span>
            </Label>
            <Controller
              name="employeeNo"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  className={`form-control external-filter-input ${errors.employeeNo ? 'is-invalid' : ''}`}
                  type="text"
                  placeholder="Enter Employee No."
                />
              )}
            />
            {errors.employeeNo && (
              <span className="text-danger">{errors.employeeNo.message}</span>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Col>
            <button type="button" className="w-100 cu-cancel" onClick={toggle}>
              Close
            </button>
          </Col>
          <Col>
            <button
              type="submit"
              className="w-100 cu-add"
              disabled={!isValid || isPending}
            >
              {isPending ? 'Changing...' : 'Change'}
            </button>
          </Col>
        </ModalFooter>
      </form>
    </Modal>
  );
}

export default ChangeEmployeeModal;
